import {
  FamilyMember,
  FamilyMembersStorage,
  CreateFamilyMemberInput,
  UpdateFamilyMemberInput,
} from '@/types/family-member';
import { ScopedAsyncStorage } from '@/lib/storage';
import { FamilyMemberColorPalette } from '@/lib/family/FamilyMemberColorPalette';

const STORAGE_KEY = '@family_members';
const DEFAULT_MEMBER_COLOR = FamilyMemberColorPalette.getDefaultColor().hex;
const STORAGE_VERSION = '1.0';

/**
 * Local storage for family members using AsyncStorage
 * Auto-creates "Me" member on first access if none exist
 */
export class LocalFamilyMembersStore {
  private readonly storage: ScopedAsyncStorage;

  /**
   * Create a new LocalFamilyMembersStore instance
   * @param storage - Scoped storage instance
   */
  constructor(storage: ScopedAsyncStorage) {
    this.storage = storage;
  }

  private generateId(): string {
    return `family_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  private getDefaultStorage(): FamilyMembersStorage {
    return {
      members: {},
      version: STORAGE_VERSION,
    };
  }

  private createDefaultMeMember(): FamilyMember {
    const now = new Date().toISOString();
    return {
      id: this.generateId(),
      name: 'Me',
      color: DEFAULT_MEMBER_COLOR,
      notes: null,
      dateOfBirth: null,
      profilePicture: null,
      isMe: true,
      createdAt: now,
      updatedAt: now,
    };
  }

  /**
   * Load storage data from AsyncStorage
   * Ensures "Me" member exists
   */
  private async loadStorage(): Promise<FamilyMembersStorage> {
    try {
      const data = await this.storage.getItem(STORAGE_KEY);
      if (!data) {
        // First access - create default storage with "Me"
        const storage = this.getDefaultStorage();
        const meMember = this.createDefaultMeMember();
        storage.members[meMember.id] = meMember;
        await this.saveStorage(storage);
        return storage;
      }

      const storage: FamilyMembersStorage = JSON.parse(data);

      // Migrate existing members: fill missing fields
      let needsSave = false;
      for (const id of Object.keys(storage.members)) {
        const member = storage.members[id];
        if (member.profilePicture === undefined) {
          member.profilePicture = null;
          needsSave = true;
        }
        if (!member.color) {
          member.color = DEFAULT_MEMBER_COLOR;
          needsSave = true;
        }
        // Migrate legacy string notes → string[] (split on newlines)
        if (typeof (member.notes as unknown) === 'string') {
          const legacy = member.notes as unknown as string;
          member.notes = legacy
            .split('\n')
            .map(line => line.trim())
            .filter(line => line.length > 0);
          if (member.notes.length === 0) {
            member.notes = null;
          }
          needsSave = true;
        }
      }

      // Ensure "Me" member exists
      const hasMe = Object.values(storage.members).some(m => m.isMe);
      if (!hasMe) {
        const meMember = this.createDefaultMeMember();
        storage.members[meMember.id] = meMember;
        needsSave = true;
      }

      if (needsSave) {
        await this.saveStorage(storage);
      }

      return storage;
    } catch (error) {
      console.error('Error loading family members storage:', error);
      return this.getDefaultStorage();
    }
  }

  /**
   * Save storage data to AsyncStorage
   */
  private async saveStorage(storage: FamilyMembersStorage): Promise<void> {
    await this.storage.setItem(STORAGE_KEY, JSON.stringify(storage));
  }

  /**
   * Get all family members sorted by creation date
   */
  async getAll(): Promise<FamilyMember[]> {
    const storage = await this.loadStorage();
    return Object.values(storage.members).sort(
      (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
    );
  }

  /**
   * Get a specific family member by ID
   */
  async getById(id: string): Promise<FamilyMember | null> {
    const storage = await this.loadStorage();
    return storage.members[id] || null;
  }

  /**
   * Get the "Me" family member
   */
  async getMe(): Promise<FamilyMember | null> {
    const storage = await this.loadStorage();
    return Object.values(storage.members).find(m => m.isMe) || null;
  }

  /**
   * Create a new family member
   */
  async create(input: CreateFamilyMemberInput): Promise<FamilyMember> {
    const storage = await this.loadStorage();

    const now = new Date().toISOString();
    const member: FamilyMember = {
      id: this.generateId(),
      name: input.name.trim(),
      color: input.color,
      notes: input.notes ?? null,
      dateOfBirth: input.dateOfBirth ?? null,
      profilePicture: input.profilePicture ?? null,
      isMe: input.isMe ?? false,
      createdAt: now,
      updatedAt: now,
    };

    storage.members[member.id] = member;
    await this.saveStorage(storage);

    return member;
  }

  /**
   * Update an existing family member
   * Cannot update isMe flag
   */
  async update(id: string, updates: UpdateFamilyMemberInput): Promise<FamilyMember | null> {
    const storage = await this.loadStorage();
    const existing = storage.members[id];

    if (!existing) return null;

    const updated: FamilyMember = {
      ...existing,
      ...updates,
      name: updates.name !== undefined ? updates.name.trim() : existing.name,
      updatedAt: new Date().toISOString(),
    };

    storage.members[id] = updated;
    await this.saveStorage(storage);

    return updated;
  }

  /**
   * Delete a family member
   * Cannot delete the "Me" member
   */
  async delete(id: string): Promise<boolean> {
    const storage = await this.loadStorage();
    const member = storage.members[id];

    if (!member) return false;
    if (member.isMe) return false; // Cannot delete "Me"

    delete storage.members[id];
    await this.saveStorage(storage);

    return true;
  }

  /**
   * Get multiple family members by IDs
   */
  async getByIds(ids: string[]): Promise<FamilyMember[]> {
    const storage = await this.loadStorage();
    return ids
      .map(id => storage.members[id])
      .filter((member): member is FamilyMember => member !== undefined);
  }

  /**
   * Check if a color is already in use
   */
  async isColorInUse(color: string, excludeId?: string): Promise<boolean> {
    const storage = await this.loadStorage();
    return Object.values(storage.members).some(
      m => m.color?.toLowerCase() === color.toLowerCase() && m.id !== excludeId
    );
  }
}
